import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { LayoutGrid, List, LockKeyhole, RotateCcw } from "lucide-react";
import { toast } from "react-hot-toast";

import EmptyState from "@/components/layout/EmptyState";
import PageHeader from "@/components/layout/PageHeader";
import RenderCatalogAssetCard from "@/components/render/catalog/RenderCatalogAssetCard";
import RenderCatalogAssetTable from "@/components/render/catalog/RenderCatalogAssetTable";
import RenderCatalogFilters from "@/components/render/catalog/RenderCatalogFilters";
import RenderCatalogSummaryCards from "@/components/render/catalog/RenderCatalogSummaryCards";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { api } from "@/lib/api";
import type { RenderAssetListItem } from "@/lib/api-types";
import {
  buildPaginationItems,
  buildRenderAssetPath,
  parsePositiveInt,
} from "@/lib/render-catalog";
import type { AssetStorefrontMode } from "@/lib/storefront";
import { useAuthStore } from "@/stores/auth";

type QueryUpdateOptions = {
  resetPage?: boolean;
  replace?: boolean;
  clearSelectedAssetPath?: boolean;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function RenderCatalogPage() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentUser = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const detailPanelRef = useRef<HTMLDivElement | null>(null);
  const shouldRevealDetailRef = useRef(false);
  const [collectionComboboxOpen, setCollectionComboboxOpen] = useState(false);
  const assetRouteMatch = useMatch(
    "/assets/clothes/:collection/:gender/:kind/:slotId/:drawableLocalIndex/:meshPartIndex/:assetId",
  );
  const selectedAssetID = (assetRouteMatch?.params.assetId ?? "").trim();
  const selectedDrawableSignature = assetRouteMatch
    ? [
        assetRouteMatch.params.collection ?? "",
        assetRouteMatch.params.gender ?? "",
        assetRouteMatch.params.kind ?? "",
        assetRouteMatch.params.slotId ?? "",
        assetRouteMatch.params.drawableLocalIndex ?? "",
      ].join("|")
    : "";
  const canManagePricing = currentUser?.role === "admin";

  const search = searchParams.get("search") ?? "";
  const pedModel = searchParams.get("pedModel") ?? "";
  const collection = searchParams.get("collection") ?? "";
  const kind = searchParams.get("kind") ?? "";
  const slotId = searchParams.get("slotId") ?? "";
  const drawableLocalIndex = searchParams.get("drawableLocalIndex") ?? "";
  const viewMode = searchParams.get("view") === "table" ? "table" : "grid";
  const page = parsePositiveInt(searchParams.get("page")) ?? 1;
  const pageSize = parsePositiveInt(searchParams.get("limit")) ?? 10;

  const updateQuery = (
    updates: Record<string, string | number | undefined>,
    options: QueryUpdateOptions = {},
  ) => {
    const next = new URLSearchParams(searchParams);
    if (options.resetPage) {
      next.delete("page");
    }
    for (const [key, value] of Object.entries(updates)) {
      if (value === undefined || value === "") {
        next.delete(key);
        continue;
      }
      next.set(key, String(value));
    }

    const nextSearch = next.toString();
    if (options.clearSelectedAssetPath && selectedAssetID) {
      navigate(
        {
          pathname: "/assets/clothes",
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: options.replace ?? true },
      );
      return;
    }

    setSearchParams(next, { replace: options.replace ?? true });
  };

  const navigateToAsset = (
    nextAssetPath: string,
    options: { replace?: boolean; clearTextureIndex?: boolean } = {},
  ) => {
    const nextParams = new URLSearchParams(searchParams);
    if (options.clearTextureIndex) {
      nextParams.delete("textureIndex");
    }
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: nextAssetPath || "/assets/clothes",
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: options.replace ?? false },
    );
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (viewMode === "table") {
      next.set("view", "table");
    }
    navigate(
      {
        pathname: "/assets/clothes",
        search: next.toString() ? `?${next.toString()}` : "",
      },
      { replace: true },
    );
  };

  const filters = useMemo(
    () => ({
      search,
      pedModel,
      collection,
      kind,
      slotId: slotId ? Number(slotId) : undefined,
      drawableLocalIndex: drawableLocalIndex ? Number(drawableLocalIndex) : undefined,
      page,
      limit: pageSize,
    }),
    [collection, drawableLocalIndex, kind, page, pageSize, pedModel, search, slotId],
  );

  const activeFilterCount = useMemo(
    () =>
      [search, pedModel, collection, kind, slotId, drawableLocalIndex].filter(
        (value) => value.trim() !== "",
      ).length,
    [collection, drawableLocalIndex, kind, pedModel, search, slotId],
  );

  const metaQuery = useQuery({
    queryKey: ["asset-library-clothes-meta"],
    queryFn: api.getAssetLibraryClothesMeta,
  });

  const assetsQuery = useQuery({
    queryKey: ["asset-library-clothes-items", filters],
    queryFn: () => api.getAssetLibraryClothesItems(filters),
  });

  const updatePriceMutation = useMutation({
    mutationFn: ({
      asset,
      priceInput,
      priceType,
    }: {
      asset: RenderAssetListItem;
      priceInput: string;
      priceType: AssetStorefrontMode;
    }) => {
      const nextPrice = Number(priceInput || "0");
      if (!Number.isFinite(nextPrice) || nextPrice < 0) {
        throw new Error("Giá không hợp lệ");
      }

      return api.updateAssetLibraryClothesTexturePrice(
        asset.id,
        Number.isFinite(asset.defaultTextureIndex)
          ? asset.defaultTextureIndex
          : 0,
        nextPrice,
        priceType,
        "Table bulk update",
        "all",
      );
    },
    onSuccess: (result, variables) => {
      toast.success(
        `Đã cập nhật ${result.updated} texture linked cho ${variables.asset.assetName}`,
      );
      queryClient.invalidateQueries({
        queryKey: ["asset-library-clothes-items"],
      });
      queryClient.invalidateQueries({
        queryKey: ["asset-library-clothes-item"],
      });
      queryClient.invalidateQueries({ queryKey: ["stats-overview"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const assetPaginationItems = assetsQuery.data
    ? buildPaginationItems(
        assetsQuery.data.pagination.page,
        assetsQuery.data.pagination.totalPages,
      )
    : [];
  const selectedListAssetID = useMemo(() => {
    if (!assetsQuery.data || !selectedDrawableSignature) {
      return selectedAssetID;
    }

    const matchingItem = assetsQuery.data.items.find((item) => {
      const itemSignature = [
        item.collectionName || "base",
        item.pedGender,
        item.kind,
        String(item.slotId),
        String(item.drawableLocalIndex),
      ].join("|");
      return itemSignature === selectedDrawableSignature;
    });

    return matchingItem?.id ?? selectedAssetID;
  }, [assetsQuery.data, selectedAssetID, selectedDrawableSignature]);

  const revealDetailPanel = () => {
    if (
      typeof window === "undefined" ||
      window.matchMedia("(min-width: 1024px)").matches
    ) {
      return;
    }

    detailPanelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleSelectAsset = (asset: RenderAssetListItem) => {
    if (asset.id === selectedListAssetID) {
      revealDetailPanel();
      return;
    }

    shouldRevealDetailRef.current = true;
    navigateToAsset(buildRenderAssetPath(asset), { clearTextureIndex: true });
  };

  useEffect(() => {
    if (!selectedAssetID || !shouldRevealDetailRef.current) {
      return;
    }

    shouldRevealDetailRef.current = false;
    const frame = window.requestAnimationFrame(() => {
      revealDetailPanel();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedAssetID]);

  if (metaQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading clothes library...</div>;
  }

  if (metaQuery.isError || !metaQuery.data) {
    return <div className="text-sm text-red-500">Không tải được render catalog metadata.</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={isAuthenticated ? "Asset library" : "Public catalog"}
        title="Clothes library"
        actions={
          <>
            {activeFilterCount > 0 ? (
              <Button
                variant="outline"
                onClick={resetFilters}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset filters
              </Button>
            ) : null}
            {!isAuthenticated ? (
              <Button asChild variant="outline">
                <Link to="/login">
                  <LockKeyhole className="mr-2 h-4 w-4" />
                  Sign in to manage pricing
                </Link>
              </Button>
            ) : null}
          </>
        }
      />

      <RenderCatalogSummaryCards meta={metaQuery.data} />

      <div className="grid items-start gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <RenderCatalogFilters
          meta={metaQuery.data}
          values={{
            search,
            pedModel,
            collection,
            kind,
            slotId,
            drawableLocalIndex,
          }}
          activeFilterCount={activeFilterCount}
          collectionComboboxOpen={collectionComboboxOpen}
          onCollectionComboboxOpenChange={setCollectionComboboxOpen}
          onUpdateQuery={updateQuery}
          onReset={resetFilters}
        />

        <div className="space-y-6">
          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.18fr)_minmax(360px,0.82fr)]">
            <Card className="rounded-none border-0 bg-transparent shadow-none">
              <CardHeader className="px-0 pt-0 pb-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle>Results</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {assetsQuery.data
                        ? `${assetsQuery.data.pagination.total.toLocaleString()} drawable khớp bộ lọc hiện tại.`
                        : "Đang nạp danh sách asset."}
                    </p>
                  </div>
                  {assetsQuery.data ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="inline-flex rounded-xl border border-border/80 bg-background/70 p-1">
                        <Button
                          type="button"
                          size="sm"
                          variant={viewMode === "grid" ? "default" : "ghost"}
                          className="h-8"
                          onClick={() =>
                            updateQuery({ view: undefined }, { replace: true })
                          }
                        >
                          <LayoutGrid className="mr-2 h-4 w-4" />
                          Grid
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={viewMode === "table" ? "default" : "ghost"}
                          className="h-8"
                          onClick={() =>
                            updateQuery({ view: "table" }, { replace: true })
                          }
                        >
                          <List className="mr-2 h-4 w-4" />
                          Table
                        </Button>
                      </div>
                      <label className="flex items-center gap-2 rounded-full border border-border/80 px-3 py-1 text-xs text-muted-foreground">
                        <span>Items / page</span>
                        <select
                          value={String(pageSize)}
                          onChange={(event) => {
                            const nextLimit = Number(event.target.value);
                            updateQuery(
                              {
                                limit:
                                  Number.isFinite(nextLimit) && nextLimit !== 10
                                    ? nextLimit
                                    : undefined,
                              },
                              { resetPage: true, replace: false },
                            );
                          }}
                          className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                        >
                          {PAGE_SIZE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="rounded-full border border-border/80 px-3 py-1 text-xs text-muted-foreground">
                        Page {assetsQuery.data.pagination.page} / {assetsQuery.data.pagination.totalPages}
                      </div>
                    </div>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-4 p-0">
                {assetsQuery.isLoading ? (
                  <div className="text-sm text-muted-foreground">Loading render assets...</div>
                ) : assetsQuery.isError || !assetsQuery.data ? (
                  <div className="text-sm text-red-500">Không tải được render assets.</div>
                ) : assetsQuery.data.items.length === 0 ? (
                  <EmptyState
                    title="Không có asset khớp bộ lọc"
                    description="Hãy nới bộ lọc hoặc reset về trạng thái mặc định để xem lại toàn bộ catalog."
                  />
                ) : (
                  <>
                    {viewMode === "table" ? (
                      <RenderCatalogAssetTable
                        assets={assetsQuery.data.items}
                        selectedAssetID={selectedListAssetID}
                        onSelect={handleSelectAsset}
                        isAdmin={canManagePricing}
                        savingAssetID={
                          updatePriceMutation.isPending
                            ? updatePriceMutation.variables?.asset.id ?? null
                            : null
                        }
                        onSavePrice={({ asset, priceInput, priceType }) =>
                          updatePriceMutation.mutate({
                            asset,
                            priceInput,
                            priceType,
                          })
                        }
                      />
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                        {assetsQuery.data.items.map((asset) => (
                          <RenderCatalogAssetCard
                            key={asset.id}
                            asset={asset}
                            isSelected={selectedListAssetID === asset.id}
                            onSelect={handleSelectAsset}
                          />
                        ))}
                      </div>
                    )}

                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            href="#"
                            disabled={!assetsQuery.data.pagination.hasPrev}
                            onClick={(event) => {
                              event.preventDefault();
                              if (!assetsQuery.data?.pagination.hasPrev) {
                                return;
                              }
                              updateQuery(
                                {
                                  page: Math.max(
                                    1,
                                    assetsQuery.data.pagination.page - 1,
                                  ),
                                },
                                { replace: false },
                              );
                            }}
                          />
                        </PaginationItem>
                        {assetPaginationItems.map((item, index) => (
                          <PaginationItem key={`${item}-${index}`}>
                            {item === "ellipsis" ? (
                              <PaginationEllipsis />
                            ) : (
                              <PaginationLink
                                href="#"
                                isActive={item === assetsQuery.data.pagination.page}
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (item === assetsQuery.data.pagination.page) {
                                    return;
                                  }
                                  updateQuery({ page: item }, { replace: false });
                                }}
                              >
                                {item}
                              </PaginationLink>
                            )}
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            href="#"
                            disabled={!assetsQuery.data.pagination.hasNext}
                            onClick={(event) => {
                              event.preventDefault();
                              if (!assetsQuery.data.pagination.hasNext) {
                                return;
                              }
                              updateQuery(
                                {
                                  page: assetsQuery.data.pagination.page + 1,
                                },
                                { replace: false },
                              );
                            }}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </>
                )}
              </CardContent>
            </Card>

            <div ref={detailPanelRef} className="scroll-mt-24 xl:sticky xl:top-20">
              <Card className="overflow-hidden rounded-none border-0 bg-transparent shadow-none">
                <CardHeader className="px-0 pt-0 pb-4">
                  <CardTitle>Detail panel</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 p-0 xl:max-h-[calc(100vh-11rem)] xl:overflow-y-auto xl:pr-1">
                  {selectedAssetID ? (
                    <Outlet />
                  ) : (
                    <EmptyState
                      title="Chưa chọn asset"
                      description="Chọn một card ở danh sách bên trái để xem preview, texture variants, DDS files và pricing controls."
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
